import * as React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import { startCase, isEmpty } from "lodash";
import { StyledTableRow } from "./TableRow";
import Pagination from "./Pagination";

const visuallyHidden = {
  border: 0,
  margin: -1,
  padding: 0,
  width: "1px",
  height: "1px",
  overflow: "hidden",
  position: "absolute",
  whiteSpace: "nowrap",
  clip: "rect(0 0 0 0)",
};

export default function TableView({
  rows,
  link,
  checkbox = false,
  getNext,
  count,
  query,
  setOpenDeleteDialog,
  setOpenEditDialog,
  setSelectedIDs,
  onSelectItem,
  urlId = "id",
  data,
  pagination,
  align = {},
  style = {}
}) {
  const headCells = getHeadCells() || [
    {
      id: null,
      label: <>No data was found.</>,
      align: "center",
    },
  ];

  function getHeadCells() {
    let tableHead = [];
    if (rows && rows[0]) {
      Object.keys(rows[0]).forEach((key) => {
        if (key !== "id") {
          tableHead.push({ id: key, label: key === "action" ? "": startCase(key) });
        }
      });

      return tableHead;
    }
    return false;
  }

  function descendingComparator(a, b, orderBy) {
    if (a[orderBy]?.props?.children?.props?.children > b[orderBy]?.props?.children?.props?.children) {
      return -1;
    }
    if (a[orderBy]?.props?.children?.props?.children < b[orderBy]?.props?.children?.props?.children) {
      return 1;
    }

    if (a[orderBy] && b[orderBy]) {
      if (b[orderBy] < a[orderBy]) {
        return -1;
      }
      if (b[orderBy] > a[orderBy]) {
        return 1;
      }
    }

    return 0;
  }

  function getComparator(order, orderBy) {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  // This method is created for cross-browser compatibility, if you don't
  // need to support IE11, you can use Array.prototype.sort() directly
  function stableSort(array, comparator, orderBy) {
    let newArray = [...array];

    if (orderBy) {
      newArray = newArray.sort((a, b) => {
        if (!a[orderBy]) return 1;
        if (!b[orderBy]) return -1;

        return 0;
      })
    }

    const stabilizedThis = newArray.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    if (query) {
      return newArray.filter((item) => {
        for (const [key, value] of Object.entries(item)) {
          if (
            value &&
            value.toString().toLowerCase().startsWith(query.toLowerCase())
          ) {
            return value;
          }
        }
      });
    }
    return stabilizedThis.map((el) => el[0]);
  }

  function EnhancedTableHead(props) {
    const {
      onSelectAllClick,
      order,
      orderBy,
      numSelected,
      rowCount,
      onRequestSort,
      align
    } = props;
    const createSortHandler = (property) => (event) => {
      onRequestSort(event, property);
    };

    return (
      <TableHead>
        <StyledTableRow>
          {headCells[0].id !== null && checkbox && (
            <TableCell padding="checkbox">
              <Checkbox
                color="primary"
                indeterminate={numSelected > 0 && numSelected < rowCount}
                checked={rowCount > 0 && numSelected === rowCount}
                onChange={onSelectAllClick}
              />
            </TableCell>
          )}
          {headCells.map((headCell) => (
            <TableCell
              key={headCell.id}
              align={headCell.align || align[headCell.id] || "left"}
              padding={headCell.disablePadding ? "none" : "normal"}
              sortDirection={orderBy === headCell.id ? order : false}
            >
              <TableSortLabel
                hideSortIcon
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : "asc"}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={{ ...visuallyHidden }}>
                    {order === "desc"
                      ? "sorted descending"
                      : "sorted ascending"}
                  </Box>
                ) : null}
              </TableSortLabel>
            </TableCell>
          ))}
        </StyledTableRow>
      </TableHead>
    );
  }

  EnhancedTableHead.propTypes = {
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    onSelectAllClick: PropTypes.func.isRequired,
    order: PropTypes.oneOf(["asc", "desc"]).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
  };

  const EnhancedTableToolbar = (props) => {
    // if (numSelected > 0) {
    //   return (
    //     <Toolbar
    //       sx={{
    //         pl: { sm: 2 },
    //         pr: { xs: 1, sm: 1 },
    //         ...(numSelected > 0 && {
    //           bgcolor: (theme) =>
    //             alpha(
    //               theme.palette.primary.main,
    //               theme.palette.action.activatedOpacity
    //             ),
    //         }),
    //       }}
    //     >
    //       {numSelected > 0 ? (
    //         <Typography
    //           sx={{ flex: "1 1 100%" }}
    //           color="inherit"
    //           variant="subtitle1"
    //           component="div"
    //         >
    //           {numSelected} selected
    //         </Typography>
    //       ) : (
    //         <Typography
    //           sx={{ flex: "1 1 100%" }}
    //           variant="h6"
    //           id="tableTitle"
    //           component="div"
    //         >
    //           {" "}
    //         </Typography>
    //       )}
    //     </Toolbar>
    //   );
    // }
    return <></>;
  };

  EnhancedTableToolbar.propTypes = {
    numSelected: PropTypes.number.isRequired,
  };

  function EnhancedTable() {
    const [order, setOrder] = React.useState("asc");
    const [orderBy, setOrderBy] = React.useState("calories");
    const [selected, setSelected] = React.useState([]);

    const handleRequestSort = (event, property) => {
      const isAsc = orderBy === property && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
    };

    const handleSelectAllClick = (event) => {
      if (event.target.checked) {
        const newSelecteds = rows.map((n) => n.id);
        setSelected(newSelecteds);
        setSelectedIDs(newSelecteds);
        return;
      }
      setSelected([]);
      setSelectedIDs([]);
    };

    const handleClick = (event, name) => {
      const selectedIndex = selected.indexOf(name);
      let newSelected = [];

      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selected, name);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selected.slice(1));
      } else if (selectedIndex === selected.length - 1) {
        newSelected = newSelected.concat(selected.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selected.slice(0, selectedIndex),
          selected.slice(selectedIndex + 1)
        );
      }

      setSelected(newSelected);
      setSelectedIDs(newSelected);
    };

    const handleLink = (event, id, moreThanOneId = false) => {
      if (onSelectItem) {
        return onSelectItem(id);
      }
      // allows highlighting of text in the table
      let selection = window.getSelection().toString();
      if (selection.length < 1) {
        event.preventDefault();
        if (link.includes("~id~")) {
          let newlink = link.replace("~id~", `/${id}/`);
          window.open(newlink);
        } else {
          if (moreThanOneId) window.open(`${link}/${id.join("/")}/`);
          else window.open(`${link}/${id}/`);
        }
      }
    };

    const handleChangePage = (event, newPage) => {
      getNext && getNext({ page: newPage + 1, page_size: pagination?.page_size });
      // setPage(newPage)
    };

    const handleChangeRowsPerPage = (event) => {
      getNext({ page: 1, page_size: event.target.value });
      // setRowsPerPage(parseInt(event.target.value, 10));
      // setPage(0)
    };

    const isSelected = (name) => selected.indexOf(name) !== -1;

    return (
      <Box sx={{ width: "100%" }}>
        <Paper sx={{ width: "100%", mb: 2 }}>
          <EnhancedTableToolbar numSelected={selected.length} />
          <TableContainer>
            <Table
              sx={{ minWidth: 750 }}
              aria-labelledby="tableTitle"
              // size={"small"}
            >
              <EnhancedTableHead
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={rows.length}
                align={align}
              />
              <TableBody>
                {/* if you don't need to support IE11, you can replace the `stableSort` call with:
                   rows.slice().sort(getComparator(order, orderBy)) */}
                {stableSort(rows, getComparator(order, orderBy), orderBy)
                  .map((row, index) => {
                    const isItemSelected = isSelected(row.id);
                    const labelId = `enhanced-table-checkbox-${index}`;

                    return (
                      <StyledTableRow
                        hover
                        role="checkbox"
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        key={row.id}
                        selected={isItemSelected}
                        sx={{ cursor: "pointer" }}
                      >
                        {checkbox && (
                          <TableCell padding="checkbox">
                            <Checkbox
                              color="primary"
                              checked={isItemSelected}
                              inputProps={{
                                "aria-labelledby": labelId,
                              }}
                              onClick={(event) => handleClick(event, row.id)}
                            />
                          </TableCell>
                        )}

                        {Object.keys(row).map((oneKey, i) => {
                          if (oneKey === "id") {
                            return null;
                          } else
                            return (
                              <TableCell
                                onClick={(e) => {
                                  if (urlId !== "id") {
                                    let mappedField = urlId.map(
                                      (item) => row[item]
                                    );
                                    handleLink(e, mappedField, true);
                                  } else handleLink(e, row.id);
                                }}
                                key={i}
                                align={align[oneKey] || "left"}
                                sx={style[oneKey]}
                              >
                                {row[oneKey]}
                              </TableCell>
                            );
                        })}
                      </StyledTableRow>
                    );
                  })}
                {/* {emptyRows > 0 && (
                  <TableRow
                    style={{
                      height: (dense ? 33 : 53) * emptyRows,
                    }}
                  >
                    <TableCell colSpan={6} />
                  </TableRow>
                )} */}
              </TableBody>
            </Table>
          </TableContainer>
          {!isEmpty(data) && <Pagination
              count={count}
              pagination={pagination}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />}
        </Paper>
      </Box>
    );
  }

  return EnhancedTable();
}
